import { z } from 'zod';
/**
 * Post-trial capture (DEV-17324, memory-architecture §6.9): the
 * "morning-after inspection", automated. After a benchmark trial completes,
 * the sim's own dataset says what was seeded, found, mishandled, and missed;
 * the capture pass diffs it deterministically, phrases at most a handful of
 * lessons with ONE small structured model call, and writes them into the
 * run's memory namespace so the NEXT trial can use them. Test-then-train is
 * structural: capture runs after a trial is scored and before the next
 * trial's threads launch.
 */
/** One deterministic diff finding — computed from the run dataset, no model. */
export const TrialDiffEntrySchema = z.object({
    /** Stable event identity from the dataset (`event:<id>`). */
    key: z.string(),
    /** What went wrong. */
    kind: z.enum(['missed', 'wrong_verdict']),
    /** The sim's event kind ("stuck_robot", "coffee_machine_on", ...). */
    issueType: z.string(),
    /** The sim's waypoint name for where it happened, when known. */
    near: z.string().nullable(),
    /** The scenario's answer key for the event. */
    groundTruth: z.string().nullable(),
    /** What the agent actually concluded/did, when recorded. */
    agentResolution: z.string().nullable(),
    /** The sim's outcome string for the event ('expired', 'resolved', ...). */
    outcome: z.string().nullable(),
});
/** Cap on lessons per trial — the rate-distortion budget, not a target. */
export const trialCaptureMaxLessons = 5;
/**
 * The phrasing call's output contract. Each lesson obeys the rate-distortion
 * rule: the DECISION and WHY, never the raw description. "Check the heater
 * closet on every close; it was missed twice" is a lesson; "the heater was
 * on at 21:41" is not.
 */
export const TrialLessonSchema = z.object({
    /** The lesson itself, phrased for the next trial's planner. */
    content: z.string().min(20).max(600),
    /**
     * The sim waypoint name the lesson is about (from the diff's `near`
     * vocabulary), when place-specific. Resolved to a cortex place through the
     * PlaceAlias table at write time; omitted = a mission-wide lesson.
     */
    placeAlias: z.string().min(1).max(120).optional(),
    /** The issue type the lesson generalizes over, when type-specific. */
    issueType: z.string().min(1).max(120).optional(),
});
/**
 * Parse bound is deliberately LOOSER than the semantic cap: models overshoot
 * list caps constantly, and a hard schema max would discard ALL phrased
 * lessons on exactly the messy trials with the most to teach. The service
 * slices to trialCaptureMaxLessons after parse.
 */
export const TrialCaptureLessonsSchema = z.object({
    lessons: z.array(TrialLessonSchema).max(20),
});
/** Actor subject stamped on every capture write and its audit events. */
export const trialCaptureSubject = 'service:trial-capture';
//# sourceMappingURL=trial-capture.js.map